/**
 * Intercom Web SDK Wrapper
 *
 * This module provides a web implementation of Intercom that matches the
 * native @intercom/intercom-react-native API surface. The web version uses
 * the Intercom JavaScript API via the global window.Intercom function.
 *
 * The Intercom SDK is loaded and initialized by useIntercomInitializer,
 * which should be called early in the app lifecycle.
 *
 * See: https://developers.intercom.com/installing-intercom/web/methods/
 */

interface UserAttributes {
  email: string;
  userId: string;
}

// Extend Window interface to include Intercom
declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  }
}

/**
 * Safely calls window.Intercom with the given arguments.
 * Returns a promise that resolves when the operation completes.
 */
const callIntercom = (...args: unknown[]): Promise<void> =>
  new Promise((resolve) => {
    if (typeof globalThis.window.Intercom === "function") {
      globalThis.window.Intercom(...args);
      resolve();
    } else {
      // If Intercom is not loaded, resolve immediately as a no-op
      // This prevents errors during initialization or if the feature is disabled
      resolve();
    }
  });

/**
 * Boots Intercom, carrying over the current window.intercomSettings so the launcher's current visibility survives the boot.
 * `boot` re-reads intercomSettings, so any earlier hide/show must also be mirrored into intercomSettings,
 * otherwise a boot triggered later (e.g. by the deferred user identify) resets the launcher to the initializer's default (hidden).
 */
const bootIntercom = async (attrs?: UserAttributes): Promise<void> => {
  const settings = globalThis.window.intercomSettings ?? {};
  await callIntercom("boot", {
    ...settings,
    ...(attrs ? { email: attrs.email, user_id: attrs.userId } : {}),
  });
};

const Intercom = {
  /**
   * Gets the count of unread conversations.
   * Note: The web API doesn't directly expose this, so we return 0.
   * Real unread count is shown in the messenger badge automatically.
   */
  getUnreadConversationCount: async (): Promise<number> => {
    // The web Intercom automatically shows unread count in the messenger
    // There's no direct API to get this count, so we return 0
    return 0;
  },

  /**
   * Checks if a user is currently logged in to Intercom.
   */
  isUserLoggedIn: async (): Promise<boolean> => {
    if (typeof globalThis.window.Intercom !== "function") {
      return false;
    }

    return new Promise((resolve) => {
      globalThis.window.Intercom?.("getVisitorId", (visitorId: string) => {
        // If we have a visitor ID, a user is logged in
        resolve(Boolean(visitorId));
      });
    });
  },

  /**
   * Logs in an unidentified (anonymous) user.
   */
  loginUnidentifiedUser: async (): Promise<void> => {
    // For web, we just boot Intercom without user attributes
    await bootIntercom();
  },

  /**
   * Logs in a user with their attributes (email and user ID).
   */
  loginUserWithUserAttributes: async (attrs: UserAttributes): Promise<void> => {
    await bootIntercom(attrs);
  },

  /**
   * Logs out the current user from Intercom.
   */
  logout: async (): Promise<void> => {
    await callIntercom("shutdown");
  },

  /**
   * Opens the Intercom messenger.
   */
  present: async (): Promise<void> => {
    await callIntercom("show");
  },

  /**
   * Sets the bottom padding for the Intercom messenger.
   * Note: The web messenger is typically positioned fixed, so this may not apply.
   */
  setBottomPadding: async (padding: number): Promise<void> => {
    // Web Intercom doesn't have a direct equivalent to setBottomPadding
    // The messenger is positioned via CSS and doesn't need this adjustment
    // This is a no-op for compatibility with the native API
    await callIntercom("update", { vertical_padding: padding });
  },

  /**
   * Sets the visibility of in-app messages.
   */
  setInAppMessageVisibility: async (visibility: string): Promise<void> => {
    // Web Intercom controls visibility via hide_default_launcher (the launcher button).
    // Persist it into intercomSettings, so a later boot (e.g. the deferred user identify) doesn't reset the launcher to the stale default.
    if (visibility !== Visibility.GONE && visibility !== Visibility.VISIBLE) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const hide_default_launcher = visibility === Visibility.GONE;
    globalThis.window.intercomSettings = {
      ...globalThis.window.intercomSettings,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      hide_default_launcher,
    };
    // eslint-disable-next-line @typescript-eslint/naming-convention
    await callIntercom("update", { hide_default_launcher });
  },

  /**
   * Sets the user JWT for identity verification.
   */
  setUserJwt: async (token: string): Promise<void> => {
    await callIntercom("update", { user_hash: token });
  },
};

const Visibility = {
  GONE: "gone",
  VISIBLE: "visible",
};

export { Visibility };
export default Intercom;
